import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { 
  FaTachometerAlt, 
  FaProjectDiagram, 
  FaUsers, 
  FaUserCog,
  FaCog,
  FaChevronLeft,
  FaChevronRight,
  FaSignOutAlt
} from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import IconWrapper from '../common/IconWrapper';
import Navbar from '../common/Navbar';

// Animations
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const Container = styled.div`
  display: flex;
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  position: relative;
  
  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 20%, rgba(246, 139, 33, 0.03) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(20, 19, 64, 0.03) 0%, transparent 50%);
    pointer-events: none;
    z-index: 0;
  }
`;

const Sidebar = styled.aside<{ isOpen: boolean }>`
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  width: ${({ isOpen }) => (isOpen ? '250px' : '70px')};
  min-width: ${({ isOpen }) => (isOpen ? '250px' : '70px')};
  max-width: ${({ isOpen }) => (isOpen ? '250px' : '70px')};
  background: linear-gradient(180deg, #141340 0%, #1a1a4a 50%, #2d2d6b 100%);
  color: white;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  overflow: hidden;
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 20%, rgba(246, 139, 33, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    transform: translateX(${({ isOpen }) => (isOpen ? '0' : '-100%')});
    width: 250px;
    box-shadow: ${({ isOpen }) => (isOpen ? '4px 0 20px rgba(0, 0, 0, 0.3)' : 'none')};
  }
`;

const SidebarHeader = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ isOpen }) => (isOpen ? 'space-between' : 'center')};
  padding: 1.5rem 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  z-index: 2;
  background: rgba(255, 255, 255, 0.02);
`;

const LogoContainer = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  .logo-text {
    display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
    font-weight: 700;
    font-size: 1.3rem;
    color: #F68B21;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    animation: ${fadeIn} 0.3s ease-out;
  }
`;

const Logo = styled.div`
  background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.2rem;
  box-shadow: 0 4px 12px rgba(246, 139, 33, 0.3);
  transition: all 0.3s ease;
  overflow: hidden;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(246, 139, 33, 0.4);
  }
`;

const LogoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
`;

const LogoFallback = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.2rem;
  color: white;
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    color: white;
    background-color: rgba(255, 255, 255, 0.1);
    transform: scale(1.1);
  }
`;

const SidebarMenu = styled.ul`
  list-style: none;
  padding: 1rem 0;
  position: relative;
  z-index: 2;
  flex: 1;
  overflow-y: auto;
  margin: 0;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(246, 139, 33, 0.5);
    border-radius: 2px;
    
    &:hover {
      background: rgba(246, 139, 33, 0.7);
    }
  }
`;

const SidebarMenuItem = styled.li<{ isActive: boolean; isOpen: boolean }>`
  margin-bottom: 0.5rem;
  animation: ${slideIn} 0.3s ease-out;
  
  a {
    display: flex;
    align-items: center;
    padding: ${({ isOpen }) => (isOpen ? '0.75rem 1rem' : '0.75rem')};
    justify-content: ${({ isOpen }) => (isOpen ? 'flex-start' : 'center')};
    color: ${({ isActive }) => (isActive ? 'white' : 'rgba(255, 255, 255, 0.7)')};
    text-decoration: none;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border-left: 4px solid ${({ isActive }) => (isActive ? '#F68B21' : 'transparent')};
    background: ${({ isActive }) => 
      isActive 
        ? 'linear-gradient(135deg, rgba(246, 139, 33, 0.2) 0%, rgba(246, 139, 33, 0.1) 100%)' 
        : 'transparent'
    };
    position: relative;
    
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
      background: linear-gradient(180deg, #F68B21 0%, #e67c1a 100%);
      transform: scaleY(${({ isActive }) => (isActive ? 1 : 0)});
      transition: transform 0.3s ease;
    }
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
      color: white;
      transform: translateX(4px);
      
      &::before {
        transform: scaleY(1);
      }
    }
    
    .menu-icon {
      font-size: 1.2rem;
      min-width: ${({ isOpen }) => (isOpen ? '1.5rem' : 'auto')};
      margin-right: ${({ isOpen }) => (isOpen ? '1rem' : '0')};
      transition: all 0.3s ease;
    }
    
    .menu-text {
      display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
      white-space: nowrap;
      font-weight: 500;
      animation: ${fadeIn} 0.3s ease-out;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 150px;
    }
  }
`;

const SidebarFooter = styled.div<{ isOpen: boolean }>`
  padding: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
  background: rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 2;
  flex-shrink: 0;
  
  .developer-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
  }
  
  .developer-name {
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.8rem;
  }
  
  .developer-tagline {
    font-size: 0.65rem;
    color: rgba(255, 255, 255, 0.6);
    font-style: italic;
  }
  
  .copyright {
    font-size: 0.6rem;
    color: rgba(255, 255, 255, 0.5);
    margin-top: 0.25rem;
  }
`;

const Main = styled.main<{ isSidebarOpen: boolean }>`
  flex: 1;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-left: ${({ isSidebarOpen }) => (isSidebarOpen ? '250px' : '70px')};
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const ContentWrapper = styled.div`
  padding: 2rem;
  animation: ${slideIn} 0.6s ease-out;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const MobileOverlay = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};
  transition: all 0.3s ease;
  backdrop-filter: blur(2px);
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const HRLayout: React.FC = () => {
  const location = useLocation();
  const { logout } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [logoError, setLogoError] = React.useState(false);
  
  React.useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setIsSidebarOpen(false);
      } else {
        setIsSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Call once on mount

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  const handleLogoError = () => {
    setLogoError(true);
  };

  // Check if the current path matches the menu item path
  const isActive = (path: string) => {
    if (path === '/hr' && location.pathname === '/hr') {
      return true;
    }
    return location.pathname.startsWith(path) && path !== '/hr';
  };

  const menuItems = [
    { path: '/hr', icon: <IconWrapper icon={FaTachometerAlt} className="menu-icon" />, text: 'Dashboard' },
    { path: '/hr/projects', icon: <IconWrapper icon={FaProjectDiagram} className="menu-icon" />, text: 'Projects' },
    { path: '/hr/staff', icon: <IconWrapper icon={FaUsers} className="menu-icon" />, text: 'Staff Management' },
    { path: '/hr/profile', icon: <IconWrapper icon={FaUserCog} className="menu-icon" />, text: 'Profile' },
    { path: '/hr/settings', icon: <IconWrapper icon={FaCog} className="menu-icon" />, text: 'Settings' },
  ];

  const handleLogout = () => {
    logout();
  };

  return (
    <Container>
      <Sidebar isOpen={isSidebarOpen}>
        <SidebarHeader isOpen={isSidebarOpen}>
          <LogoContainer isOpen={isSidebarOpen}>
            <Logo>
              {!logoError ? (
                <LogoImage 
                  src="/pfrs.jpeg" 
                  alt="PFRS Logo" 
                  onError={handleLogoError}
                />
              ) : (
                <LogoFallback>P</LogoFallback>
              )}
            </Logo>
            <span className="logo-text">PFRS</span>
          </LogoContainer>
          <ToggleButton onClick={toggleSidebar}>
            {isSidebarOpen ? <IconWrapper icon={FaChevronLeft} /> : <IconWrapper icon={FaChevronRight} />}
          </ToggleButton>
        </SidebarHeader>
        
        <SidebarMenu>
          {menuItems.map((item, index) => (
            <SidebarMenuItem key={index} isActive={isActive(item.path)} isOpen={isSidebarOpen}>
              <Link to={item.path} onClick={closeSidebar}>
                {item.icon}
                <span className="menu-text">{item.text}</span>
              </Link>
            </SidebarMenuItem>
          ))}
          
          {/* Logout button */}
          <SidebarMenuItem isActive={false} isOpen={isSidebarOpen}>
            <Link to="#" onClick={handleLogout}>
              <IconWrapper icon={FaSignOutAlt} className="menu-icon" />
              <span className="menu-text">Logout</span>
            </Link>
          </SidebarMenuItem>
        </SidebarMenu>
        
        <SidebarFooter isOpen={isSidebarOpen}>
          <div className="developer-info">
            <div className="developer-name">Developed by Sandfish</div>
            <div className="developer-tagline">Building Digital Solutions</div>
            <div className="copyright">
              &copy; {new Date().getFullYear()} PFRS
            </div>
          </div>
        </SidebarFooter>
      </Sidebar>
      
      <MobileOverlay isVisible={isMobile && isSidebarOpen} onClick={closeSidebar} />
      <Main isSidebarOpen={isSidebarOpen}>
        <Navbar toggleSidebar={toggleSidebar} />
        <ContentWrapper>
        <Outlet />
        </ContentWrapper>
      </Main>
    </Container>
  );
};

export default HRLayout; 